// Whiteboard geometry for free rotation / resize (Creative Freedom upgrade).
// Additive optional field `rotation` (degrees, clockwise, about the object's own centre) on rect/ellipse/text/image.
// Lines, arrows and paths have no `rotation`: rotating them rewrites their points, so endpoints stay independent.
import { bbox, resizeObj } from './whiteboardShapes';

export const ROTATES_BY_FIELD = new Set(['rect', 'ellipse', 'text', 'image']);
const RAD = Math.PI / 180;

export const normDeg = (d) => {
  let r = ((d % 360) + 360) % 360;
  if (r > 180) r -= 360;
  return Math.round(r * 10) / 10;
};

export function rotPt(x, y, cx, cy, deg) {
  const c = Math.cos(deg * RAD), s = Math.sin(deg * RAD);
  return [cx + (x - cx) * c - (y - cy) * s, cy + (x - cx) * s + (y - cy) * c];
}

export const centerOf = (o) => { const b = bbox(o); return { x: b.x + b.w / 2, y: b.y + b.h / 2 }; };

// Rotate `o` by `deg` about (cx, cy). Used for single objects (about their centre) and whole groups.
export function rotateObj(o, deg, cx, cy) {
  if (o.type === 'line' || o.type === 'arrow') {
    const [x1, y1] = rotPt(o.x1, o.y1, cx, cy, deg), [x2, y2] = rotPt(o.x2, o.y2, cx, cy, deg);
    return { ...o, x1, y1, x2, y2 };
  }
  if (o.type === 'path') return { ...o, points: o.points.map(([x, y]) => rotPt(x, y, cx, cy, deg)) };
  const c = centerOf(o);
  const [nx, ny] = rotPt(c.x, c.y, cx, cy, deg);
  return { ...o, x: o.x + (nx - c.x), y: o.y + (ny - c.y), rotation: normDeg((o.rotation || 0) + deg) || undefined };
}

// Axis-aligned bounds of the object as drawn (rotation included) — for multi-select boxes.
export function drawnBox(o) {
  const b = bbox(o), r = ROTATES_BY_FIELD.has(o.type) ? o.rotation || 0 : 0;
  if (!r) return b;
  const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
  const pts = [[b.x, b.y], [b.x + b.w, b.y], [b.x, b.y + b.h], [b.x + b.w, b.y + b.h]].map(([x, y]) => rotPt(x, y, cx, cy, r));
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const x = Math.min(...xs), y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

const HANDLE_DIR = { nw: [-1, -1], n: [0, -1], ne: [1, -1], e: [1, 0], se: [1, 1], s: [0, 1], sw: [-1, 1], w: [-1, 0] };

// Drag a corner / edge handle of a (possibly rotated) object to world point p; the opposite side stays put.
export function resizeToPoint(o, handle, p) {
  const b = bbox(o), [hx, hy] = HANDLE_DIR[handle];
  const th = (ROTATES_BY_FIELD.has(o.type) ? o.rotation || 0 : 0) * RAD, cos = Math.cos(th), sin = Math.sin(th);
  const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
  const ax = (-hx * b.w) / 2, ay = (-hy * b.h) / 2;
  const Ax = cx + ax * cos - ay * sin, Ay = cy + ax * sin + ay * cos;
  const dx = (p.x - Ax) * cos + (p.y - Ay) * sin, dy = -(p.x - Ax) * sin + (p.y - Ay) * cos;
  const w = hx ? Math.max(6, Math.abs(dx)) : b.w, h = hy ? Math.max(6, Math.abs(dy)) : b.h;
  const ox = hx ? (Math.sign(dx) || 1) * (w / 2) : 0, oy = hy ? (Math.sign(dy) || 1) * (h / 2) : 0;
  const ncx = Ax + ox * cos - oy * sin, ncy = Ay + ox * sin + oy * cos;
  return resizeObj(o, b, { x: ncx - w / 2, y: ncy - h / 2, w, h });
}

export const angleAt = (c, p) => Math.atan2(p.y - c.y, p.x - c.x) / RAD;

// Direction of a line / arrow in degrees (0 = pointing right, clockwise positive).
export const lineAngle = (o) => normDeg(Math.atan2(o.y2 - o.y1, o.x2 - o.x1) / RAD);
